import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { partnerFetch } from "@/lib/partner-client";

interface OutcomeRow {
  id: string;
  slug: string;
  name: string;
  status: string;
  price_tokens: number;
  price_kind: string;
  delivery_mode: string;
  partner_slug: string;
}

export function OutcomesListPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["dev", "outcomes"],
    queryFn: async () => {
      const res = await partnerFetch("/v1/catalog?limit=100");
      if (!res.ok) throw new Error(`catalog fetch: ${res.status}`);
      return (await res.json()) as { items: OutcomeRow[] };
    },
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <header className="flex items-center justify-between">
        <div>
          <Link to="/" className="text-sm text-neutral-500 hover:underline">
            ← Dashboard
          </Link>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">Outcomes</h1>
        </div>
      </header>

      <p className="mt-4 text-sm text-neutral-500">
        Register and update outcomes via the SDK:{" "}
        <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs dark:bg-neutral-900">
          appsumo schema push
        </code>{" "}
        or call{" "}
        <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs dark:bg-neutral-900">
          PUT /v1/catalog/outcomes/:slug
        </code>{" "}
        directly. New outcomes start in <strong>pending</strong> status and
        are published after AppSumo admin review.
      </p>

      {isLoading ? (
        <div className="mt-8 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-900"
            />
          ))}
        </div>
      ) : error ? (
        <p className="mt-8 text-sm text-red-500">
          {(error as Error).message}
        </p>
      ) : (data?.items.length ?? 0) === 0 ? (
        <p className="mt-8 rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
          You haven't published any outcomes yet.
        </p>
      ) : (
        <ul className="mt-8 divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
          {data!.items.map((o) => (
            <li
              key={o.id}
              className="grid grid-cols-12 gap-4 bg-white px-5 py-4 text-sm dark:bg-neutral-950"
            >
              <div className="col-span-5">
                <p className="font-medium">{o.name}</p>
                <p className="font-mono text-xs text-neutral-500">{o.slug}</p>
              </div>
              <div className="col-span-2 text-xs text-neutral-500">
                {o.delivery_mode} · {o.price_kind}
              </div>
              <div className="col-span-2 text-right">
                <span className="font-medium">{o.price_tokens}</span>{" "}
                <span className="text-xs text-neutral-500">tokens</span>
              </div>
              <div className="col-span-3 text-right">
                <Badge status={o.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Badge({ status }: { status: string }) {
  const cls =
    status === "published"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
      : status === "pending"
        ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
        : status === "suspended"
          ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"
          : "bg-neutral-100 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-400";
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs ${cls}`}>
      {status}
    </span>
  );
}
